import { Controller } from "@hotwired/stimulus";
import {
  errorDetail,
  formatPln,
  getJSON,
  listResults,
  sendJSON,
} from "../api.js";

// Koszyk: wysuwany drawer zasilany z DRF (GET /api/cart/) + add/remove/checkout.
//
// Scope kontrolera = <body> strony (block body_attrs) — dzięki temu przycisk
// koszyka w nav i przyciski "+ Dodaj" w wierszach tabeli trafiają w akcje.
// Mutacje NIE mają fallbacku "udawanego sukcesu": błąd POST = brak zmiany UI
// (+ komunikat), bo koszyk to rezerwacje i pieniądze.
export default class extends Controller {
  static targets = [
    "overlay",
    "drawer",
    "count",
    "empty",
    "content",
    "success",
    "net",
    "vat",
    "gross",
    "items",
    "error",
    "checkoutBtn",
    "walletOptOut",
    "termsModal",
  ];
  static values = {
    count: Number,
    url: String, // GET  /api/cart/
    addUrl: String, // POST /api/cart/add/      {lead, subcategory} — kafelek (pk pary); backend (reserve_tile) dobiera pasujący tag-listing
    removeUrl: String, // POST /api/cart/remove/   {listing_id}
    checkoutUrl: String, // POST /api/orders/checkout/
    acceptTermsUrl: String, // POST /api/terms/accept/
  };

  connect() {
    // Po powrocie z płatności: ?payment=success => pokaż potwierdzenie (nie nadpisuj loadem).
    const params = new URLSearchParams(window.location.search);
    this.showingSuccess = params.get("payment") === "success";
    if (this.showingSuccess) {
      this.open();
      this.#showState("success");
      // Zdejmij parametr z URL — reload/nawigacja wstecz nie ma ponownie pokazywać sukcesu.
      params.delete("payment");
      const query = params.toString();
      window.history.replaceState(
        {},
        "",
        query ? `?${query}` : window.location.pathname,
      );
    }
    if (this.urlValue) this.load();
  }

  async load() {
    try {
      this.#render(await getJSON(this.urlValue));
    } catch {
      // np. 400 "Brak organizacji" — zostaw stan pusty; strona dalej działa.
    }
  }

  open() {
    if (this.hasOverlayTarget) {
      this.overlayTarget.hidden = false;
      this.drawerTarget.hidden = false;
    }
  }

  close() {
    if (this.hasOverlayTarget) {
      this.overlayTarget.hidden = true;
      this.drawerTarget.hidden = true;
    }
    // Zamknięcie potwierdzenia = koniec stanu sukcesu — drawer wraca do koszyka (nowe zakupy możliwe).
    if (this.showingSuccess) {
      this.showingSuccess = false;
      if (this.hasCheckoutBtnTarget) this.checkoutBtnTarget.disabled = false;
      this.#showState(this.countValue > 0 ? "content" : "empty");
    }
  }

  // "+ Dodaj" w wierszu ORAZ w modalu podglądu (delegacja klikiem — patrz
  // preview_controller.js#addToCart). Kafelek = (lead, subcategory), OBA pk z
  // data-cart-lead-param / data-cart-subcategory-param. Katalog jest add-only: kafelek
  // może odpowiadać >1 tag-listingowi, więc "cofnięcie" tego samego kliknięcia nie ma
  // jednoznacznego listing_id do usunięcia — usuwanie robi się z drawera koszyka
  // (cart#remove, per-listing, patrz #render/#reflectButton niżej).
  async add(event) {
    const { lead, subcategory, mode } = event.params;
    const btn = event.currentTarget;
    // ins-slots#7: include mode only when set (picker shown). BUR rows have no mode param
    // → omitting preserves the pre-slots contract (backend defaults to exclusivity).
    const body = { lead, subcategory };
    if (mode) body.mode = mode;
    const res = await sendJSON("POST", this.addUrlValue, body);
    if (!res.ok) {
      this.#flashError(
        errorDetail(res.data, "Nie udało się zaktualizować koszyka."),
      );
      return;
    }
    this.#reflectButton(btn, true);
    await this.load();
  }

  async remove(event) {
    const id = event.params.id;
    const res = await sendJSON("POST", this.removeUrlValue, { listing_id: id });
    if (!res.ok) return;
    const btn = document.querySelector(
      `.ls-add-btn[data-cart-id-param="${id}"]`,
    );
    if (btn) this.#reflectButton(btn, false);
    await this.load();
  }

  async checkout() {
    if (this.hasCheckoutBtnTarget) this.checkoutBtnTarget.disabled = true;
    // Portfel domyślnie WŁĄCZONY; checkbox „Nie używaj portfela" = świadomy opt-out.
    const skipWallet = this.hasWalletOptOutTarget && this.walletOptOutTarget.checked;
    const res = await sendJSON("POST", this.checkoutUrlValue, {
      use_wallet: !skipWallet,
    });
    if (res.ok) {
      if (res.data?.redirect_url) {
        // Zewnętrzna bramka (P24): jedziemy na stronę płatności.
        window.location.assign(res.data.redirect_url);
        return;
      }
      // Brak redirectu = płatność uznana od razu (dev AutoPaid / kredyt) — pokaż potwierdzenie.
      this.showingSuccess = true;
      this.#showState("success");
      this.open();
      await this.load(); // koszyk jest już pusty (rezerwacje sprzedane)
      return;
    }
    if (this.hasCheckoutBtnTarget) this.checkoutBtnTarget.disabled = false;
    // Brama regulaminu (art. 7 RODO): checkout omija middleware /api/, więc backstop jest tu.
    // 403 z code=terms_required → pokaż modal regulaminu zamiast generycznego błędu.
    if (res.status === 403 && res.data?.code === "terms_required") {
      if (this.hasTermsModalTarget) this.termsModalTarget.hidden = false;
      return;
    }
    await this.load(); // rezerwacje mogły wygasnąć — odśwież zawartość
    this.#flashError(
      errorDetail(res.data, "Nie udało się rozpocząć płatności."),
    ); // PO renderze (render czyści stare błędy)
  }

  // Akceptacja regulaminu z modala checkout → zapis akceptacji, zamknięcie modala, ponowny checkout.
  async acceptTerms() {
    const res = await sendJSON("POST", this.acceptTermsUrlValue, {});
    if (!res.ok) {
      this.#flashError("Nie udało się zapisać akceptacji regulaminu.");
      return;
    }
    if (this.hasTermsModalTarget) this.termsModalTarget.hidden = true;
    await this.checkout();
  }

  closeTerms() {
    if (this.hasTermsModalTarget) this.termsModalTarget.hidden = true;
  }

  // Klik w treść modala nie propaguje do overlaya (który zamyka modal).
  stop(event) {
    event.stopPropagation();
  }

  countValueChanged(value) {
    this.countTargets.forEach((el) => {
      el.textContent = value;
    });
  }

  // --- prywatne ---

  #render(data) {
    this.countValue = data.count;
    if (this.hasErrorTarget) this.errorTarget.hidden = true; // udany render czyści stary komunikat błędu
    if (this.hasNetTarget) this.netTarget.textContent = formatPln(data.net);
    if (this.hasVatTarget) this.vatTarget.textContent = formatPln(data.vat);
    if (this.hasGrossTarget)
      this.grossTarget.textContent = formatPln(data.gross);

    if (this.hasItemsTarget) {
      this.itemsTarget.textContent = "";
      const template = document.getElementById("cart-item-template");
      for (const item of listResults(data)) {
        const row = template.content.firstElementChild.cloneNode(true);
        row.dataset.cartItem = item.id;
        row.querySelector('[data-field="cat"]').textContent =
          item.category_path;
        row.querySelector('[data-field="code"]').textContent = item.code;
        row.querySelector('[data-field="price"]').textContent = formatPln(
          item.price_pln,
        );
        // ins-slots#7: mode label (Slot / Wyłączność).
        // WHY conditional: the cart API currently returns only RESERVED listings (exclusivity)
        // and does NOT include a `mode` field — slot reservations live in SlotReservation and
        // are NOT yet surfaced by list_cart. Leave the element hidden when mode is absent rather
        // than inferring "exclusivity" from position — a wrong label is worse than no label.
        // The element unhides automatically once the backend adds the field.
        const modeEl = row.querySelector('[data-field="mode"]');
        if (modeEl && item.mode) {
          modeEl.textContent = item.mode === "slot" ? "Slot" : "Wyłączność";
          modeEl.hidden = false;
        }
        row
          .querySelector("[data-action='cart#remove']")
          .setAttribute("data-cart-id-param", item.id);
        this.itemsTarget.appendChild(row);
      }
    }
    if (!this.showingSuccess)
      this.#showState(data.count > 0 ? "content" : "empty");
  }

  #reflectButton(btn, inCart) {
    btn.classList.toggle("is-in-cart", inCart);
    const glyph = btn.querySelector(".ls-mono");
    const label = btn.querySelector("[data-cart-label]");
    if (glyph) glyph.textContent = inCart ? "✓" : "+";
    if (label) label.textContent = inCart ? "W koszyku" : "Dodaj";
    const row = btn.closest(".ls-lead-row");
    if (row) row.classList.toggle("is-selected", inCart);
  }

  #showState(state) {
    if (this.hasEmptyTarget) this.emptyTarget.hidden = state !== "empty";
    if (this.hasContentTarget) this.contentTarget.hidden = state !== "content";
    if (this.hasSuccessTarget) this.successTarget.hidden = state !== "success";
  }

  #flashError(message) {
    if (!this.hasErrorTarget) return;
    this.errorTarget.textContent = message;
    this.errorTarget.hidden = false;
  }
}
